import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTeamStore } from "../state/teamStore";
import { useRunStore } from "../state/runStore";
import { formatLapTime, formatSetupName as setupFilename } from "../utils/formatters";
import type { RunSummary } from "../types";

/* ---------------------------------------------------------------------------
   QualifyingBoard — Best single-lap per driver from non-Race sessions
   --------------------------------------------------------------------------- */

interface QualEntry {
  rank: number;
  userId: string;
  displayName: string;
  setupName: string;
  runId: string;
  bestLapTime: number;
  greenLaps: number | null;
  eventType: string;
  weather: RunSummary["weather"];
  sessionDate: string | null;
}

const EVENT_COLORS: Record<string, string> = {
  Test: "var(--flag-purple)",
  Practice: "var(--live)",
  Qualifying: "var(--flag-orange)",
};

const skyIcons: Record<string, string> = {
  Clear: "☀️",
  "Partly Cloudy": "⛅",
  "Mostly Cloudy": "🌥️",
  Overcast: "☁️",
  Rain: "🌧️",
};

export default function QualifyingBoard() {
  const { teamId, trackWeekId } = useParams<{ teamId?: string; trackWeekId?: string }>();
  const { activeTeamId } = useTeamStore();
  const { runs, selectedTrackWeekId, fetchRuns } = useRunStore();
  const effectiveTeamId = teamId || activeTeamId;
  const effectiveTrackWeekId = trackWeekId || selectedTrackWeekId;

  useEffect(() => {
    if (effectiveTeamId && effectiveTrackWeekId) fetchRuns(effectiveTeamId, effectiveTrackWeekId);
  }, [effectiveTeamId, effectiveTrackWeekId, fetchRuns]);

  // Filter to non-Race runs only, then find best lap per driver
  const qualEntries = useMemo(() => {
    const nonRace = runs.filter((r) => r.eventType !== "Race" && r.bestLapTime != null && r.bestLapTime > 0);

    // Best lap per driver
    const bestByDriver = new Map<string, RunSummary>();
    for (const r of nonRace) {
      const existing = bestByDriver.get(r.userId);
      if (!existing || (r.bestLapTime! < existing.bestLapTime!)) {
        bestByDriver.set(r.userId, r);
      }
    }

    // Sort by best lap time and rank
    const sorted = Array.from(bestByDriver.values())
      .sort((a, b) => (a.bestLapTime ?? Infinity) - (b.bestLapTime ?? Infinity));

    return sorted.map<QualEntry>((r, i) => ({
      rank: i + 1,
      userId: r.userId,
      displayName: r.displayName || "Unknown",
      setupName: r.setupName,
      runId: r.id,
      bestLapTime: r.bestLapTime!,
      greenLaps: r.greenLaps,
      eventType: r.eventType || "Test",
      weather: r.weather,
      sessionDate: r.sessionDate,
    }));
  }, [runs]);

  // Also collect ALL non-Race runs for the full attempts table
  const allAttempts = useMemo(() => {
    return runs
      .filter((r) => r.eventType !== "Race" && r.bestLapTime != null && r.bestLapTime > 0)
      .sort((a, b) => (a.bestLapTime ?? Infinity) - (b.bestLapTime ?? Infinity));
  }, [runs]);

  if (qualEntries.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "3rem", color: "var(--text-secondary)" }}>
        <div style={{ fontSize: "1.1rem", fontWeight: 600, marginBottom: "0.5rem" }}>No qualifying data</div>
        <div style={{ fontSize: "0.85rem" }}>Upload Test or Practice .ibt files to see qualifying times. Race runs are excluded.</div>
      </div>
    );
  }

  const fastestTime = qualEntries[0]?.bestLapTime;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {/* Tab content under the WeekWorkspace hero — no page title */}

      {/* Leaderboard */}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={styles.tableHeader}>
          <div>
            <div style={{ fontFamily: "var(--font-body)", fontSize: "0.85rem", fontWeight: 500, color: "var(--text-secondary)" }}>Best qualifying lap</div>
            <div style={{ fontFamily: "var(--font-display)", fontSize: "1.1rem", fontWeight: 700, color: "var(--text-strong)", marginTop: "0.15rem" }}>Driver leaderboard</div>
          </div>
          <span style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>{qualEntries.length} drivers</span>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <Th>Pos</Th>
              <Th align="left">Driver</Th>
              <Th align="left">Setup</Th>
              <Th>Best Lap</Th>
              <Th>Gap</Th>
              <Th>Laps</Th>
              <Th>Session</Th>
              <Th>Weather</Th>
            </tr>
          </thead>
          <tbody>
            {qualEntries.map((e) => {
              const gap = e.rank === 1 ? null : e.bestLapTime - fastestTime;
              const ec = EVENT_COLORS[e.eventType] || "var(--text-muted)";
              return (
                <tr key={e.runId} style={{ borderBottom: "1px solid var(--rule-faint)" }}>
                  <Td style={{ fontWeight: 700, color: e.rank <= 3 ? "var(--live)" : "var(--text-secondary)" }}>
                    P{e.rank}
                  </Td>
                  <Td align="left" style={{ fontWeight: 600, color: "var(--text-strong)" }}>
                    {e.displayName}
                  </Td>
                  <Td align="left" style={{ fontSize: "0.8125rem", color: "var(--text-secondary)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={e.setupName}>
                    {setupFilename(e.setupName)}
                  </Td>
                  <Td style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", color: e.rank === 1 ? "var(--live)" : "var(--text-strong)" }}>
                    {formatLapTime(e.bestLapTime)}
                  </Td>
                  <Td style={{ fontVariantNumeric: "tabular-nums", color: gap ? "var(--flag-red)" : "var(--text-muted)" }}>
                    {gap ? `+${gap.toFixed(3)}` : "—"}
                  </Td>
                  <Td>{e.greenLaps ?? "—"}</Td>
                  <Td>
                    <span style={{
                      fontSize: "0.8125rem", fontWeight: 600,
                      padding: "1px 6px", borderRadius: 3,
                      color: ec, background: `color-mix(in srgb, ${ec} 9%, transparent)`, border: `1px solid color-mix(in srgb, ${ec} 19%, transparent)`,
                    }}>
                      {e.eventType}
                    </span>
                  </Td>
                  <Td>
                    <WeatherInline weather={e.weather} />
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* All attempts */}
      {allAttempts.length > qualEntries.length && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={styles.tableHeader}>
            <div>
              <div style={{ fontFamily: "var(--font-body)", fontSize: "0.85rem", fontWeight: 500, color: "var(--text-secondary)" }}>All attempts</div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: "1.1rem", fontWeight: 700, color: "var(--text-strong)", marginTop: "0.15rem" }}>Every non-race run ranked</div>
            </div>
            <span style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>{allAttempts.length} runs</span>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <Th>#</Th>
                <Th align="left">Driver</Th>
                <Th align="left">Setup</Th>
                <Th>Best Lap</Th>
                <Th>Gap</Th>
                <Th>Session</Th>
                <Th>Weather</Th>
              </tr>
            </thead>
            <tbody>
              {allAttempts.map((r, i) => {
                const gap = i === 0 ? null : (r.bestLapTime! - allAttempts[0].bestLapTime!);
                const et = r.eventType || "Test";
                const ec = EVENT_COLORS[et] || "var(--text-muted)";
                return (
                  <tr key={r.id} style={{ borderBottom: "1px solid var(--rule-faint)" }}>
                    <Td style={{ color: "var(--text-muted)" }}>{i + 1}</Td>
                    <Td align="left" style={{ fontWeight: 600, color: "var(--text-strong)" }}>{r.displayName || "Unknown"}</Td>
                    <Td align="left" style={{ fontSize: "0.8125rem", color: "var(--text-secondary)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.setupName}>
                      {setupFilename(r.setupName)}
                    </Td>
                    <Td style={{ fontWeight: i === 0 ? 700 : 400, fontVariantNumeric: "tabular-nums", color: i === 0 ? "var(--live)" : "var(--text-strong)" }}>
                      {formatLapTime(r.bestLapTime)}
                    </Td>
                    <Td style={{ fontVariantNumeric: "tabular-nums", color: gap ? "var(--flag-red)" : "var(--text-muted)" }}>
                      {gap ? `+${gap.toFixed(3)}` : "—"}
                    </Td>
                    <Td>
                      <span style={{ fontSize: "0.8125rem", fontWeight: 600, padding: "1px 6px", borderRadius: 3, color: ec, background: `color-mix(in srgb, ${ec} 9%, transparent)`, border: `1px solid color-mix(in srgb, ${ec} 19%, transparent)` }}>
                        {et}
                      </span>
                    </Td>
                    <Td><WeatherInline weather={r.weather} /></Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Sub-components
   --------------------------------------------------------------------------- */

function WeatherInline({ weather }: { weather: RunSummary["weather"] }) {
  if (!weather) return <span style={{ color: "var(--text-muted)", fontSize: "0.8125rem" }}>—</span>;
  const icon = weather.skies ? skyIcons[weather.skies] || "" : "";
  return (
    <span style={{ fontSize: "0.8125rem", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
      {icon} {weather.airTemp != null ? `${Math.round(weather.airTemp)}°` : ""}
      {weather.humidity != null ? ` ${Math.round(weather.humidity)}%` : ""}
    </span>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: "left" | "center" }) {
  return (
    <th style={{
      padding: "0.5rem 0.6rem",
      textAlign: align || "center",
      fontFamily: "var(--font-body)",
      fontSize: "0.8125rem",
      fontWeight: 500,
      color: "var(--text-secondary)",
      borderBottom: "1px solid var(--rule)",
      whiteSpace: "nowrap",
    }}>
      {children}
    </th>
  );
}

function Td({ children, align, style, title }: { children: React.ReactNode; align?: "left" | "center"; style?: React.CSSProperties; title?: string }) {
  return (
    <td style={{
      padding: "0.45rem 0.6rem",
      textAlign: align || "center",
      fontSize: "0.8125rem",
      ...style,
    }} title={title}>
      {children}
    </td>
  );
}

const styles: Record<string, React.CSSProperties> = {
  tableHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "1rem",
    padding: "1rem 1.1rem",
    borderBottom: "1px solid var(--rule)",
    background: "var(--ink-1)",
  },
};
